import * as React from "react";
import styled, { css } from "styled-components";

import {
  dayHours,
  dayMinutes
} from "src/library/utils/time-helpers/time-helpers";
import { Select } from "src/library/components/Forms";
import { Text } from "src/library/components/Text/Text";
import { Button } from "src/library/components/Button";

interface CutoffTimeSelectionProps {
  setTime: (time: string) => void;
  value?: string;
  name: string;
  timezone?: string;
  isDefaultValue?: boolean;
  disabled?: boolean;
}

/**
 * Component for cutoff time selection
 */
export const CutoffTimeSelection: React.ReactFCWithChildren<CutoffTimeSelectionProps> = ({
  setTime, value, name, timezone, isDefaultValue, disabled
}) => {
  const { hours, minutes } = React.useMemo(() => {
    if (value) {
      const timeSplit = value.split(":");

      return ({
        hours: timeSplit[ 0 ],
        minutes: timeSplit[ 1 ]
      });
    }

    return ({
      hours: "00",
      minutes: "00"
    });
  }, [ value ]);

  if (isDefaultValue) {
    return (
      <CutoffTimeWrapper>
        <div className="cutoff-time-inner">
          <Button
            disabled={disabled}
            onClick={() => {
            // set the time to something new to pick up a change
              setTime(`${hours !== "00" ? "00" : "12"}:${minutes}`);
            }}
          >
            {`Override default: ${value}`}
          </Button>
        </div>
      </CutoffTimeWrapper>
    );
  }

  return (
    // Time cutoff.
    <CutoffTimeWrapper>
      <div className="cutoff-time-inner">
        <div className="select-box-wrapper">
          <Select
            as="select"
            value={hours}
            className="select-box select-box-hour"
            name={`${name}-hours`}
            autoHideLabels
            disabled={disabled}
            label=""
            options={dayHours.map(dayHour => {
              return {
                text: dayHour,
                value: dayHour
              };
            })}
            handleChange={selectProps => {
              setTime(`${selectProps.currentTarget.value}:${minutes}`);
            }}
          />

          <Text weight="bold">
            :
          </Text>

          <Select
            as="select"
            value={minutes}
            className="select-box select-box-minute"
            name={`${name}-minutes`}
            disabled={disabled}
            autoHideLabels
            label=""
            options={dayMinutes.map(dayMinute => {
              return {
                text: dayMinute.toString(),
                value: dayMinute.toString()
              };
            })}
            handleChange={selectProps => {
              setTime(`${hours}:${selectProps.currentTarget.value}`);
            }}
          />
        </div>

      </div>

      {timezone && (
        <Text>
          {`(${timezone})`}
        </Text>
      )}
    </CutoffTimeWrapper>
  );
};

const CutoffTimeWrapper = styled.div`
  ${({ theme }) => css`
    display: flex; 
    flex-direction: row;
    align-items: center;

    .cutoff-time-inner {
      width: 200px;
      display: flex;
      flex-direction: column;
    }

    .select-box-wrapper {
      display: flex;
      align-items: center;
    }

    .select-box {
      width: 90px;

      select {
        min-width: 0;
      }
    }

    .select-box-minute {
      padding-left: ${theme.spacing.padding.xs};
    }

    .select-box-hour {
      padding-right: ${theme.spacing.padding.xs};
    }
  `}
`;
